import { useMemo, useState } from 'react';
import { Search, Plus, Pencil } from 'lucide-react';
import { Button, Card, Input, Modal, Pill, useToast } from '@nako/ui';
import { formatMoney, stockOnHand, priceFromMarkup } from '@nako/core';
import type {
  Business,
  Profile,
  PharmacyDrug,
  DrugForm,
  DrugSchedule,
  VatTreatment,
} from '@nako/types';
import { isControlledSchedule } from '@nako/types';
import { supabase } from '@/lib/supabase';
import type { PharmacyData } from '@/pages/Pharmacy';

const FORMS: DrugForm[] = [
  'tablet',
  'capsule',
  'syrup',
  'suspension',
  'injection',
  'cream',
  'ointment',
  'drops',
  'inhaler',
  'suppository',
  'powder',
  'other',
];
const SCHEDULES: DrugSchedule[] = ['OTC', 'S1', 'S2', 'S3', 'S4'];
const VAT_TREATMENTS: VatTreatment[] = ['standard', 'zero', 'exempt'];

interface DraftDrug {
  id?: string;
  name: string;
  generic_name: string;
  strength: string;
  form: DrugForm;
  schedule: DrugSchedule;
  barcode: string;
  dispense_unit: string;
  reorder_level: string;
  cost_price: string;
  sale_price: string;
  markup_pct: string;
  vat_treatment: VatTreatment;
}

const EMPTY: DraftDrug = {
  name: '',
  generic_name: '',
  strength: '',
  form: 'tablet',
  schedule: 'OTC',
  barcode: '',
  dispense_unit: 'each',
  reorder_level: '0',
  cost_price: '',
  sale_price: '',
  markup_pct: '',
  vat_treatment: 'standard',
};

export function FormularyList({
  business,
  profile,
  data,
}: {
  business: Business;
  profile: Profile | null;
  data: PharmacyData;
}) {
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState<DraftDrug | null>(null);
  const [saving, setSaving] = useState(false);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data.drugs;
    return data.drugs.filter(
      (d) => d.name.toLowerCase().includes(q) || (d.generic_name ?? '').toLowerCase().includes(q),
    );
  }, [search, data.drugs]);

  function openEdit(d: PharmacyDrug) {
    setDraft({
      id: d.id,
      name: d.name,
      generic_name: d.generic_name ?? '',
      strength: d.strength ?? '',
      form: d.form ?? 'tablet',
      schedule: d.schedule,
      barcode: d.barcode ?? '',
      dispense_unit: d.dispense_unit,
      reorder_level: String(d.reorder_level),
      cost_price: d.cost_price != null ? String(d.cost_price) : '',
      sale_price: d.sale_price != null ? String(d.sale_price) : '',
      markup_pct: d.markup_pct != null ? String(d.markup_pct) : '',
      vat_treatment: d.vat_treatment,
    });
  }

  const suggestedPrice = useMemo(() => {
    if (!draft) return null;
    const cost = Number(draft.cost_price);
    const markup = Number(draft.markup_pct);
    if (!cost || !markup) return null;
    return priceFromMarkup(cost, markup, draft.vat_treatment);
  }, [draft]);

  async function save() {
    if (!draft) return;
    if (!draft.name.trim()) {
      toast.show('Drug name is required.', { tone: 'warning' });
      return;
    }
    setSaving(true);
    const row = {
      business_id: business.id,
      name: draft.name.trim(),
      generic_name: draft.generic_name.trim() || null,
      strength: draft.strength.trim() || null,
      form: draft.form,
      schedule: draft.schedule,
      barcode: draft.barcode.trim() || null,
      dispense_unit: draft.dispense_unit.trim() || 'each',
      reorder_level: Number(draft.reorder_level) || 0,
      cost_price: draft.cost_price ? Number(draft.cost_price) : null,
      sale_price: draft.sale_price ? Number(draft.sale_price) : null,
      markup_pct: draft.markup_pct ? Number(draft.markup_pct) : null,
      vat_treatment: draft.vat_treatment,
      requires_prescription: draft.schedule !== 'OTC' && draft.schedule !== 'S1',
    };
    const { error } = draft.id
      ? await supabase.from('pharmacy_drugs').update(row).eq('id', draft.id)
      : await supabase.from('pharmacy_drugs').insert({ ...row, created_by: profile?.id ?? null });
    setSaving(false);
    if (error) {
      toast.show(error.message, { tone: 'error' });
      return;
    }
    toast.show(draft.id ? 'Drug updated.' : 'Drug added.', { tone: 'success' });
    setDraft(null);
    await data.reload();
  }

  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        <Input
          leftAddon={<Search size={16} />}
          placeholder="Search formulary…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1"
        />
        <Button onClick={() => setDraft({ ...EMPTY })}>
          <Plus size={16} /> Add
        </Button>
      </div>

      {filtered.length === 0 ? (
        <p className="text-muted py-10 text-center text-sm">No drugs in the formulary yet.</p>
      ) : (
        <div className="grid gap-2">
          {filtered.map((d) => {
            const onHand = stockOnHand(data.batchesByDrug.get(d.id) ?? []);
            const low = onHand <= d.reorder_level;
            return (
              <Card key={d.id} className="flex items-center justify-between gap-3 p-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-ink truncate font-medium">{d.name}</span>
                    {d.strength && <span className="text-muted text-xs">{d.strength}</span>}
                    {isControlledSchedule(d.schedule) ? (
                      <Pill tone="red">{d.schedule}</Pill>
                    ) : (
                      <Pill tone="neutral">{d.schedule}</Pill>
                    )}
                    {low && <Pill tone="accent">low</Pill>}
                  </div>
                  <div className="text-muted mt-0.5 text-xs">
                    {d.generic_name ?? ''} · {d.form ?? ''} · {formatMoney(d.sale_price ?? 0)} ·{' '}
                    {onHand} {d.dispense_unit} on hand
                  </div>
                </div>
                <button onClick={() => openEdit(d)} aria-label="Edit" className="shrink-0 p-1">
                  <Pencil size={16} className="text-muted" />
                </button>
              </Card>
            );
          })}
        </div>
      )}

      <Modal
        open={draft !== null}
        onClose={() => setDraft(null)}
        title={draft?.id ? 'Edit drug' : 'Add drug'}
      >
        {draft && (
          <div className="grid max-h-[70vh] gap-3 overflow-y-auto pr-1">
            <Input
              label="Trade / brand name"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            />
            <Input
              label="Generic name (INN)"
              value={draft.generic_name}
              onChange={(e) => setDraft({ ...draft, generic_name: e.target.value })}
            />
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Strength"
                placeholder="500mg"
                value={draft.strength}
                onChange={(e) => setDraft({ ...draft, strength: e.target.value })}
              />
              <label className="grid gap-1 text-sm">
                <span className="text-ink font-medium">Form</span>
                <select
                  className="rounded-btn border-line h-10 border px-2"
                  value={draft.form}
                  onChange={(e) => setDraft({ ...draft, form: e.target.value as DrugForm })}
                >
                  {FORMS.map((f) => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="grid gap-1 text-sm">
                <span className="text-ink font-medium">Schedule</span>
                <select
                  className="rounded-btn border-line h-10 border px-2"
                  value={draft.schedule}
                  onChange={(e) => setDraft({ ...draft, schedule: e.target.value as DrugSchedule })}
                >
                  {SCHEDULES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>
              <label className="grid gap-1 text-sm">
                <span className="text-ink font-medium">VAT treatment</span>
                <select
                  className="rounded-btn border-line h-10 border px-2"
                  value={draft.vat_treatment}
                  onChange={(e) =>
                    setDraft({ ...draft, vat_treatment: e.target.value as VatTreatment })
                  }
                >
                  {VAT_TREATMENTS.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Dispense unit"
                value={draft.dispense_unit}
                onChange={(e) => setDraft({ ...draft, dispense_unit: e.target.value })}
              />
              <Input
                label="Reorder level"
                numeric
                value={draft.reorder_level}
                onChange={(e) => setDraft({ ...draft, reorder_level: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Input
                label="Cost price"
                numeric
                value={draft.cost_price}
                onChange={(e) => setDraft({ ...draft, cost_price: e.target.value })}
              />
              <Input
                label="Markup %"
                numeric
                value={draft.markup_pct}
                onChange={(e) => setDraft({ ...draft, markup_pct: e.target.value })}
              />
              <Input
                label="Sale price"
                numeric
                value={draft.sale_price}
                onChange={(e) => setDraft({ ...draft, sale_price: e.target.value })}
                hint={suggestedPrice != null ? `≈ ${formatMoney(suggestedPrice)}` : undefined}
              />
            </div>
            {suggestedPrice != null && !draft.sale_price && (
              <button
                type="button"
                onClick={() => setDraft({ ...draft, sale_price: String(suggestedPrice) })}
                className="text-left text-xs text-emerald-700 underline"
              >
                Use suggested {formatMoney(suggestedPrice)}
              </button>
            )}
            <Button onClick={() => void save()} disabled={saving}>
              {saving ? 'Saving…' : draft.id ? 'Save changes' : 'Add drug'}
            </Button>
          </div>
        )}
      </Modal>
    </div>
  );
}
